/** Aplicar TODAS as alterações pendentes aos bytes de um IFC.
 *
 *  Um só caminho para a página do IFCEdit e para o painel Dados do IFC,
 *  para os dois gravarem exactamente o mesmo ficheiro. Ordem:
 *  1. mudanças de classe (reescrevem o cabeçalho da entidade, e o
 *     PredefinedType a seguir tem de cair no enumerado certo);
 *  2. atributos;
 *  3. valores de Psets editados no painel de propriedades;
 *  4. os lotes em massa, por ordem (importar de Excel, renomear, apagar).
 *  Os lotes vêm por último porque podem mudar nomes de Psets que as
 *  edições do passo 3 ainda procuram pelo nome antigo. */

import { bytesToLatin1, latin1ToBytes } from '@viewer/lib/stepText'
import { ModeloStep } from '@viewer/lib/step/modeloStep'
import type { IfcEditSlice } from '../store/ifcEditSlice'
import { applyAttributeEdits, applyClassEdits, type AttributeEdit, type ClassEdit } from './stepPatch'
import { applyPsetEdits, type PsetEdit } from './psetPatch'
import type { IfcEnumSchema } from './ifcEnums.generated'
import { aplicarOperacoes, relatorioVazio, type Lote, type Relatorio } from './operacoesEmMassa'

export interface EdicoesPendentes {
  atributos: AttributeEdit[]
  classes: ClassEdit[]
  psets: PsetEdit[]
  lotes: Lote[]
}

type Estado = Pick<IfcEditSlice, 'ifcEditAttributeEdits' | 'ifcEditClassChanges' | 'ifcEditPsetEdits' | 'ifcEditLotes'>

export function edicoesDoEstado(s: Estado, esquema: IfcEnumSchema | null): EdicoesPendentes {
  const atributos: AttributeEdit[] = []
  for (const [guid, attrs] of Object.entries(s.ifcEditAttributeEdits)) {
    for (const [attribute, value] of Object.entries(attrs)) atributos.push({ guid, attribute, value, schema: esquema })
  }
  const psets: PsetEdit[] = []
  for (const [guid, porPset] of Object.entries(s.ifcEditPsetEdits)) {
    for (const [psetName, porProp] of Object.entries(porPset)) {
      for (const [propName, value] of Object.entries(porProp)) psets.push({ guid, psetName, propName, value })
    }
  }
  return {
    atributos,
    classes: Object.entries(s.ifcEditClassChanges).map(([guid, newClass]) => ({ guid, newClass })),
    psets,
    lotes: s.ifcEditLotes,
  }
}

export function totalDeEdicoes(e: EdicoesPendentes): number {
  return e.atributos.length + e.classes.length + e.psets.length + e.lotes.reduce((n, l) => n + l.operacoes.length, 0)
}

export interface ResultadoDeGravar {
  bytes: Uint8Array
  /** Edições do painel de propriedades aplicadas. */
  aplicadas: number
  semCorrespondencia: unknown[]
  /** Relatório das operações em massa (vazio se não houver lotes). */
  relatorio: Relatorio
}

export function aplicarEdicoes(bytes: Uint8Array, e: EdicoesPendentes): ResultadoDeGravar {
  const s1 = applyClassEdits(bytes, e.classes)
  const s2 = applyAttributeEdits(s1.bytes, e.atributos)
  const s3 = applyPsetEdits(s2.bytes, e.psets)
  let final = s3.bytes
  let relatorio = relatorioVazio()
  const operacoes = e.lotes.flatMap((l) => l.operacoes)
  if (operacoes.length > 0) {
    const m = new ModeloStep(bytesToLatin1(final))
    relatorio = aplicarOperacoes(m, operacoes)
    if (m.alterado()) final = latin1ToBytes(m.serializar())
  }
  return {
    bytes: final,
    aplicadas: s1.applied + s2.applied + s3.applied,
    semCorrespondencia: [...s1.unmatched, ...s2.unmatched, ...s3.unmatched],
    relatorio,
  }
}
